/* assets/ps-shell-picker.js */
(() => {
  const DEFAULT_STORAGE_KEY = 'ps_selected_shells_v1';
  const MOBILE_MQ = '(max-width: 760px)';

  // Cache for specs HTML per product URL
  const productSpecsCache = new Map(); // key: productUrl (absolute)|params, value: html string

  // =========================================================
  // 0) Global quote-bar offset handling (body padding)
  // =========================================================
  const barHeights = new Map(); // key: pickerEl, value: height(px)

  function setGlobalBottomOffset(px) {
    const val = px ? `${px}px` : '0px';
    document.documentElement.style.setProperty('--ps-quote-bar-h', val);
    document.body.style.paddingBottom = 'var(--ps-quote-bar-h, 0px)';
  }

  function recomputeGlobalOffset() {
    let max = 0;
    for (const h of barHeights.values()) if (typeof h === 'number' && h > max) max = h;
    setGlobalBottomOffset(max);
  }

  function updateGlobalOffsetForPicker(pickerEl, barEl) {
    if (!pickerEl) return;

    if (!barEl || barEl.hidden) {
      barHeights.set(pickerEl, 0);
      recomputeGlobalOffset();
      return;
    }

    requestAnimationFrame(() => {
      barHeights.set(pickerEl, barEl.offsetHeight || 0);
      recomputeGlobalOffset();
    });
  }

  function remeasureAllBars() {
    document.querySelectorAll('ps-shell-picker').forEach(pickerEl => {
      const bar = pickerEl.querySelector('[data-ps-quote-bar]');
      barHeights.set(pickerEl, (!bar || bar.hidden) ? 0 : (bar.offsetHeight || 0));
    });
    recomputeGlobalOffset();
  }

  function setupTabKeyboardNavigation(tablistEl) {
    const tabs = Array.from(tablistEl.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    tablistEl.addEventListener('keydown', (e) => {
      const currentIndex = tabs.findIndex(t => t === document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex = null;

      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      tabs[nextIndex].focus();
      tabs[nextIndex].click();
    });
  }

  let psResizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(psResizeTimer);
      psResizeTimer = setTimeout(remeasureAllBars, 150);
    }, { passive: true });
  window.addEventListener('orientationchange', remeasureAllBars, { passive: true });

  // =========================================================
  // 1) Storage
  // =========================================================
  function readSelected(storageKey) {
    try {
      const raw = sessionStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function writeSelected(storageKey, items) {
    sessionStorage.setItem(storageKey, JSON.stringify(items));
  }

  function normalizeItemFromCard(cardEl) {
    return {
      id: String(cardEl.getAttribute('data-product-id') || ''),
      title: String(cardEl.getAttribute('data-title') || ''),
      url: String(cardEl.getAttribute('data-url') || ''),
      sku: String(cardEl.getAttribute('data-sku') || '')
    };
  }

  function isSelected(storageKey, productId) {
    const id = String(productId);
    return readSelected(storageKey).some(x => String(x.id) === id);
  }

  function toggleSelection(storageKey, item) {
    const items = readSelected(storageKey);
    const id = String(item.id || '');
    const idx = items.findIndex(x => String(x.id) === id);

    if (idx >= 0) items.splice(idx, 1);
    else items.push({ id, title: item.title || '', url: item.url || '', sku: item.sku || '' });

    writeSelected(storageKey, items);
    return items;
  }

  function removeSelectedById(storageKey, id) {
    const items = readSelected(storageKey);
    const next = items.filter(x => String(x.id) !== String(id));
    writeSelected(storageKey, next);
    return next;
  }

  function clearSelected(storageKey) {
    writeSelected(storageKey, []);
    return [];
  }

  // =========================================================
  // 2) Hidden inputs sync
  // =========================================================
  function syncHiddenInputs(pickerEl, items) {
    if (!pickerEl) return;

    const lines = (items || []).map(x => {
      const id = String(x.id || '');
      const title = String(x.title || '').trim();
      const card = pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(id)}"]`);
      const sizeLine = (card?.getAttribute('data-size-line') || '').trim();
      if (title && sizeLine) return `${title} ${sizeLine}`;
      return title || id;
    });

    const text = lines.join('\n');
    pickerEl.querySelectorAll('[data-ps-selected-items]').forEach(el => (el.value = text));

    const jsonEls = pickerEl.querySelectorAll('[data-ps-selected-items-json]');
    if (jsonEls.length) {
      const safe = (items || []).map(x => {
        const id = String(x.id || '');
        const title = String(x.title || '');
        const card = pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(id)}"]`);
        const size = (card?.getAttribute('data-size-line') || '').trim();
        return { id, title, size };
      });
      jsonEls.forEach(el => (el.value = JSON.stringify(safe)));
    }
  }

  // =========================================================
  // 3) UI: card state
  // =========================================================
  function updateCardUI(cardEl, selected) {
    if (!cardEl) return;
    cardEl.classList.toggle('is-selected', !!selected);

    const toggleBtn = cardEl.querySelector('[data-ps-toggle]');
    if (toggleBtn) {
      toggleBtn.textContent = selected ? 'Added' : 'Add To Quote';
      toggleBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }
  }

  function refreshCards(pickerEl, items) {
    const set = new Set(items.map(x => String(x.id)));
    pickerEl.querySelectorAll('.ps-shell-picker__item').forEach(card => {
      updateCardUI(card, set.has(String(card.getAttribute('data-product-id'))));
    });
  }

  // =========================================================
  // 3.1) Tabs + has_spa checkbox
  // =========================================================
  const DEFAULT_TAB_WITH_TABS = 'medium';
  const VALID_TABS = new Set(['plunge', 'small', 'medium', 'large', 'lap', 'all']);

  function getTabStorageKey(storageKey) { return `${storageKey}__size_tab_v1`; }
  function getSpaStorageKey(storageKey) { return `${storageKey}__show_spa_v1`; }
  function hasTabsMarkup(pickerEl) { return !!pickerEl.querySelector('[data-ps-tab]'); }

  function normalizeTab(tab, tabsExist) {
    const t = String(tab || '').toLowerCase().trim();
    if (!VALID_TABS.has(t)) return tabsExist ? DEFAULT_TAB_WITH_TABS : 'all';
    if (t === 'all' && tabsExist) return DEFAULT_TAB_WITH_TABS;
    return t;
  }

  function getActiveTab(pickerEl, storageKey) {
    if (!hasTabsMarkup(pickerEl)) return 'all';
    const activeBtn = pickerEl.querySelector('[data-ps-tab].is-active');
    if (activeBtn) return normalizeTab(activeBtn.getAttribute('data-ps-tab') || DEFAULT_TAB_WITH_TABS, true);
    return normalizeTab(sessionStorage.getItem(getTabStorageKey(storageKey)) || DEFAULT_TAB_WITH_TABS, true);
  }

  function getShowSpa(pickerEl, storageKey) {
    const cb = pickerEl.querySelector('[data-ps-filter="has_spa"]');
    if (!cb) return true;
    if (typeof cb.checked === 'boolean') return !!cb.checked;
    const saved = sessionStorage.getItem(getSpaStorageKey(storageKey));
    if (saved === null) return true;
    return saved === '1';
  }

  function setActiveTabUI(pickerEl, tab) {
    if (!hasTabsMarkup(pickerEl)) return;
    pickerEl.querySelectorAll('[data-ps-tab]').forEach(btn => {
      const isActive = String(btn.getAttribute('data-ps-tab') || '').toLowerCase().trim() === tab;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  function getCardSizeGroup(card) {
    const a = (card.getAttribute('data-size-category') || '').toLowerCase().trim();
    return a || (card.getAttribute('data-size-group') || '').toLowerCase().trim();
  }

  function boolFromData(v) {
    return String(v || '0') === '1' || String(v || '').toLowerCase() === 'true';
  }

  function applyAllFilters(pickerEl, storageKey) {
    const tab = getActiveTab(pickerEl, storageKey);
    const showSpa = getShowSpa(pickerEl, storageKey);

    pickerEl.querySelectorAll('.ps-shell-picker__item').forEach(card => {
      const g = getCardSizeGroup(card);
      const hasSpa = boolFromData(card.getAttribute('data-has-spa'));
      const passTab = (tab === 'all') || (g === tab);
      const passSpa = showSpa ? true : !hasSpa;
      card.classList.toggle('is-hidden', !(passTab && passSpa));
    });

    pickerEl.querySelectorAll('[data-ps-catwrap], .ps-shell-picker__catwrap').forEach(wrap => {
      const anyVisible = [...wrap.querySelectorAll('.ps-shell-picker__item')]
        .some(el => !el.classList.contains('is-hidden'));
      wrap.classList.toggle('is-hidden', !anyVisible);
    });
  }

  function initTabs(pickerEl, storageKey) {
    if (!hasTabsMarkup(pickerEl)) {
      applyAllFilters(pickerEl, storageKey);
      const tablist = pickerEl.querySelector('[role="tablist"]');
      if (tablist) setupTabKeyboardNavigation(tablist);
      return;
    }

    const tabKey = getTabStorageKey(storageKey);
    const savedRaw = sessionStorage.getItem(tabKey) || DEFAULT_TAB_WITH_TABS;
    const saved = normalizeTab(savedRaw, true);

    if (saved !== savedRaw) sessionStorage.setItem(tabKey, saved);
    setActiveTabUI(pickerEl, saved);
    applyAllFilters(pickerEl, storageKey);

    pickerEl.addEventListener('click', (e) => {
      const tabBtn = e.target.closest('[data-ps-tab]');
      if (!tabBtn) return;
      const tab = normalizeTab(tabBtn.getAttribute('data-ps-tab') || DEFAULT_TAB_WITH_TABS, true);
      sessionStorage.setItem(tabKey, tab);
      setActiveTabUI(pickerEl, tab);
      applyAllFilters(pickerEl, storageKey);
    });
  }

  function bindSpaFilter(pickerEl, storageKey) {
    const cb = pickerEl.querySelector('[data-ps-filter="has_spa"]');
    if (!cb) return;

    const saved = sessionStorage.getItem(getSpaStorageKey(storageKey));
    if (saved !== null) cb.checked = (saved === '1');

    cb.addEventListener('change', () => {
      sessionStorage.setItem(getSpaStorageKey(storageKey), cb.checked ? '1' : '0');
      applyAllFilters(pickerEl, storageKey);
    });
  }

  // =========================================================
  // 4) Quote bar
  // =========================================================
  function ensureQuoteBar(pickerEl) {
    let bar = pickerEl.querySelector('[data-ps-quote-bar]');
    if (bar) return bar;

    bar = document.createElement('div');
    bar.className = 'ps-quote-bar';
    bar.setAttribute('data-ps-quote-bar', '');
    bar.hidden = true;

    bar.innerHTML = `
      <div class="ps-quote-bar__inner">
        <div class="ps-quote-bar__left">
          <div class="ps-quote-bar__title" data-ps-quote-bar-title>1 shell selected</div>
        </div>

        <button type="button" class="ps-btn ps-btn--primary ps-quote-bar__cta" data-ps-view-selection>
          View selection
        </button>
      </div>
    `;

    const modal = pickerEl.querySelector('.ps-product-modal');
    if (modal) pickerEl.insertBefore(bar, modal);
    else pickerEl.appendChild(bar);

    return bar;
  }

  function updateQuoteBar(pickerEl, items) {
    const bar = ensureQuoteBar(pickerEl);
    const count = items.length;
    const prev = Number(pickerEl.__psPrevCount || 0);

    if (count === 0) {
      bar.hidden = true;
      updateGlobalOffsetForPicker(pickerEl, bar);
      pickerEl.__psPrevCount = 0;
      return;
    }

    bar.hidden = false;

    const titleEl = bar.querySelector('[data-ps-quote-bar-title]');
    if (titleEl) {
      titleEl.textContent = `${count} shell${count === 1 ? '' : 's'} selected`;
    }

    // 🔥 0 → 1 : большая анимация
    if (prev === 0 && count === 1) {
      triggerQuoteBarAttention(bar);
    }

    // 👀 1 → 2 → 3… : микро-подёргивание
    if (count > prev && prev > 0) {
      bar.classList.remove('is-tick');
      void bar.offsetWidth; // reset animation
      bar.classList.add('is-tick');
    }

    pickerEl.__psPrevCount = count;
    updateGlobalOffsetForPicker(pickerEl, bar);
  }


  function triggerQuoteBarAttention(barEl) {
    if (!barEl) return;

    barEl.classList.remove('is-attention');
    // перезапуск анимации
    void barEl.offsetWidth;
    barEl.classList.add('is-attention');
  }

  // =========================================================
  // 4.5) Modal base helpers
  // =========================================================
  function openPsModal(modalEl) {
    if (!modalEl) return;

    if (document.body.__psPrevOverflow === undefined) {
      document.body.__psPrevOverflow = document.body.style.overflow || '';
    }
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('ps-modal-open');

    modalEl.hidden = false;
    modalEl.classList.add('is-open');

    modalEl.__psOnKeyDown = modalEl.__psOnKeyDown || ((ev) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        closePsModal(modalEl);
      }
    });
    document.addEventListener('keydown', modalEl.__psOnKeyDown, true);

    const closeBtn = modalEl.querySelector('.ps-modal__close,[data-ps-modal-close].ps-modal__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function closePsModal(modalEl) {
    if (!modalEl) return;
    modalEl.hidden = true;
    modalEl.classList.remove('is-open');

    // Очищаем только тяжелые объекты (3D), чтобы не вешать Chrome
    const mv = modalEl.querySelector('model-viewer');
    if (mv) mv.remove(); 

    // Возвращаем скролл страницы
    const anyVisible = document.querySelector('.ps-modal:not([hidden])');
    if (!anyVisible) {
      document.body.style.overflow = (document.body.__psPrevOverflow ?? '');
      document.documentElement.classList.remove('ps-modal-open');
    }
  }

  // =========================================================
  // 5) Product modal
  // =========================================================
  function getModalEls(pickerEl) {
    const modal = pickerEl.querySelector('.ps-product-modal');
    if (!modal) return null;

    const stageImg =
      modal.querySelector('[data-ps-gallery-stage] img') ||
      modal.querySelector('.ps-product-modal__stage img') ||
      modal.querySelector('.ps-product-modal__image img') ||
      null;

    const thumbs =
      modal.querySelector('[data-ps-gallery-thumbs]') ||
      modal.querySelector('.ps-product-modal__thumbs') ||
      null;

    return {
      modal,
      stageImg,
      thumbs,
      title: modal.querySelector('.ps-product-modal__title'),
      meta: modal.querySelector('.ps-product-modal__meta'),
      desc: modal.querySelector('.ps-product-modal__desc'),
      actions: modal.querySelector('.ps-product-modal__actions'),
      details: modal.querySelector('[data-ps-modal-details]')
    };
  }

  function openProductModal(pickerEl) {
    const els = getModalEls(pickerEl);
    if (!els) return;
    openPsModal(els.modal);
  }

  function closeProductModal(pickerEl) {
    const els = getModalEls(pickerEl);
    if (!els) return;
    closePsModal(els.modal);
  }

  // =========================================================
  // 5.1) Gallery helpers (HTML-escaped JSON)
  // =========================================================
  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch { return null; }
  }

  function normalizeGalleryFromCard(cardEl) {
    const modelUrl = cardEl.getAttribute('data-3d-url');
    const rawAttr = (cardEl.getAttribute('data-gallery') || '').trim();
    const raw = decodeHtmlEntities(rawAttr);
    const fallbackImg = (cardEl.getAttribute('data-image') || '').trim();
    const title = (cardEl.getAttribute('data-title') || '').trim();

    let out = [];
    if (raw) {
      const parsed = safeJsonParse(raw);
      if (Array.isArray(parsed)) {
        out = parsed.map(x => {
          if (typeof x === 'string') return { src: x.trim(), thumb: x.trim(), alt: title };
          if (x && typeof x === 'object') {
            const src = String(x.src || x.url || x.image || '').trim();
            return src ? { src, thumb: String(x.thumb || src).trim(), alt: String(x.alt || title).trim() } : null;
          }
          return null;
        }).filter(Boolean);
      }
    }
    if (out.length === 0 && fallbackImg) out.push({ src: fallbackImg, thumb: fallbackImg, alt: title });

    // Возвращаем 3D на вторую позицию
    if (modelUrl) {
      out.splice(1, 0, { type: 'model', src: modelUrl, thumb: fallbackImg, alt: 'Interactive 3D Model' });
    }
    return out;
  }

  function setActiveThumb(thumbsWrap, index) {
    if (!thumbsWrap) return;
    thumbsWrap.querySelectorAll('[data-ps-thumb]').forEach(btn => {
      const i = Number(btn.getAttribute('data-ps-thumb') || -1);
      btn.classList.toggle('is-active', i === index);
      btn.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  function renderProductGallery(modalEl, els, gallery, title) {
    if (!modalEl || !els || !els.thumbs) return;

    const stageWrapper = modalEl.querySelector('.ps-product-modal__stage') || (els.stageImg ? els.stageImg.parentElement : null);
    if (!stageWrapper) return;

    const items = Array.isArray(gallery) ? gallery : [];
    modalEl.__psGalleryItems = items;

    // 1. Рендерим миниатюры. Если тип 'model' — добавляем бейдж 3D
    els.thumbs.innerHTML = items.map((x, i) => {
      const isModel = x.type === 'model';
      return `
        <button type="button" class="ps-gallery-thumb" data-ps-thumb="${i}" data-is-model="${isModel}">
          <img src="${escapeAttr(x.thumb || x.src)}" alt="" loading="lazy">
          ${isModel ? '<span class="ps-3d-badge">3D</span>' : ''}
        </button>
      `;
    }).join('');

    // 2. Функция обновления главной сцены
    const updateStage = (index) => {
      const item = items[index];
      if (!item) return;

      if (item.type === 'model') {
        stageWrapper.innerHTML = `
          <model-viewer src="${item.src}" camera-controls auto-rotate shadow-intensity="1" 
            style="width:100%; height:450px; background:#f4f4f4; border-radius:8px;">
          </model-viewer>`;
      } else {
        stageWrapper.innerHTML = `<img src="${item.src}" alt="${escapeAttr(item.alt || title)}" loading="lazy">`;
      }
      
      // Важно: обновляем ссылку на активное изображение для других функций
      els.stageImg = stageWrapper.querySelector('img, model-viewer');
      setActiveThumb(els.thumbs, index);
    };

    // 3. Обработка клика
    if (!modalEl.__psGalleryBound) {
      modalEl.__psGalleryBound = true;
      els.thumbs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ps-thumb]');
        if (btn) updateStage(Number(btn.getAttribute('data-ps-thumb')));
      });
    }

    // ВСЕГДА запускаем первый кадр при вызове функции (решает проблему исчезновения)
    updateStage(0);
  }

  function buildKeyBulletsHtml(cardEl) {
    const bullets = [];

    const depthShallow = (cardEl.getAttribute('data-depth-shallow-m') || '').trim();
    const depthDeep = (cardEl.getAttribute('data-depth-deep-m') || '').trim();
    if (depthShallow && depthDeep) bullets.push(`Depth: ${depthShallow}–${depthDeep} m`);
    else if (depthShallow || depthDeep) bullets.push(`Depth: ${(depthShallow || depthDeep)} m`);

    const entryType = (cardEl.getAttribute('data-entry-type') || '').trim();
    if (entryType) bullets.push(`Entry: ${entryType}`);

    const seating = (cardEl.getAttribute('data-seating') || '').trim();
    if (seating) bullets.push(`Seating: ${seating}`);

    const hasSpa = boolFromData(cardEl.getAttribute('data-has-spa'));
    bullets.push(`Spa: ${hasSpa ? 'Optional' : 'No'}`);

    const bestFor = (cardEl.getAttribute('data-best-for') || '').trim();
    if (bestFor) bullets.push(`Best for: ${bestFor}`);

    const out = bullets.slice(0, 6);
    if (!out.length) return '';

    return `
      <ul class="ps-modal-bullets">
        ${out.map(t => `<li>${escapeHtml(t)}</li>`).join('')}
      </ul>
    `;
  }

  function fillProductModalFromCard(pickerEl, storageKey, cardEl) {
    const els = getModalEls(pickerEl);
    if (!els) return;

    const id = cardEl.getAttribute('data-product-id') || '';
    const title = cardEl.getAttribute('data-title') || '';
    const url = cardEl.getAttribute('data-url') || '';
    const sku = cardEl.getAttribute('data-sku') || '';
    const img = cardEl.getAttribute('data-image') || '';
    const sizeLine = (cardEl.getAttribute('data-size-line') || '').trim();
    const subtitle = (cardEl.getAttribute('data-card-excerpt') || '').trim();
    const modalIntro = (cardEl.getAttribute('data-modal-intro') || '').trim();

    // Gallery
    const gallery = normalizeGalleryFromCard(cardEl);
    renderProductGallery(els.modal, els, gallery, title || '');

    // Legacy fallback
    if (!els.thumbs && els.stageImg && (!gallery || !gallery.length) && img) {
      els.stageImg.src = img;
      els.stageImg.alt = title;
    }

    // Title
    if (els.title) {
      const head = sizeLine ? `${title} — ${sizeLine}` : title;
      els.title.innerHTML = `
        <span class="ps-modal-head">${escapeHtml(head)}</span>
        ${subtitle ? `<span class="ps-modal-sub">${escapeHtml(subtitle)}</span>` : ''}
      `;
    }

    // Meta
    if (els.meta) {
      els.meta.innerHTML = buildKeyBulletsHtml(cardEl);
    }

    // Desc
    if (els.desc) {
      els.desc.hidden = !modalIntro;
      els.desc.textContent = modalIntro || '';
    }

    // Actions
    if (els.actions) {
      const selected = isSelected(storageKey, id);
      els.actions.innerHTML = `
        <button
          type="button"
          class="ps-btn ps-btn--primary"
          data-ps-modal-toggle
          aria-pressed="${selected ? 'true' : 'false'}"
          data-product-id="${escapeAttr(String(id))}"
          data-title="${escapeAttr(String(title))}"
          data-url="${escapeAttr(String(url))}"
          data-sku="${escapeAttr(String(sku))}"
        >${selected ? 'Added' : 'Add to quote'}</button>

        <button type="button" class="ps-modal-more" data-ps-modal-more aria-expanded="false">
          View full specs →
        </button>
      `;
    }

    // Reset details area every open
    if (els.details) {
      els.details.hidden = true;
      els.details.innerHTML = '';
    }
  }

  function openProductModalById(pickerEl, storageKey, productId) {
    const card = pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(String(productId))}"]`);
    if (!card) return;
    fillProductModalFromCard(pickerEl, storageKey, card);
    openProductModal(pickerEl);
  }

  // =========================================================
  // 5.5) Specs loader (View full specifications)
  // =========================================================
  const SPEC_SECTION_IDS = ['ps-modal-extra-bullets', 'ps-modal-benefits'];

  function toAbsUrl(maybeRelativeUrl) {
    const u = String(maybeRelativeUrl || '').trim();
    if (!u) return '';
    try { return new URL(u, window.location.origin).toString(); }
    catch { return u; }
  }

  function extractProductHandle(productUrl) {
    if (!productUrl) return '';
    try {
      const u = new URL(productUrl, window.location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      const i = parts.indexOf('products');
      if (i >= 0 && parts[i + 1]) return parts[i + 1];
      return parts[parts.length - 1] || '';
    } catch {
      const m = String(productUrl).match(/\/products\/([^\/?#]+)/i);
      return m ? m[1] : '';
    }
  }

  function buildSectionIdUrl(productUrlAbs, sectionId) {
    if (!productUrlAbs || !sectionId) return '';
    try {
      const url = new URL(productUrlAbs);
      url.searchParams.set('section_id', sectionId);
      return url.toString();
    } catch {
      const sep = productUrlAbs.includes('?') ? '&' : '?';
      return `${productUrlAbs}${sep}section_id=${encodeURIComponent(sectionId)}`;
    }
  }

  function extractSectionInnerHtmlFromResponse(htmlText) {
    const doc = new DOMParser().parseFromString(String(htmlText || ''), 'text/html');
    const shopifySection = doc.querySelector('.shopify-section');
    if (shopifySection) return shopifySection.innerHTML.trim();
    return (doc.body?.innerHTML || '').trim();
  }

  async function loadSpecsViaSectionId(productUrlAbs) {
    // 1) Формируем URL к вашему новому шаблону с защитой от кэша
    const cacheBuster = `&_v=${Date.now()}`;
    const url = productUrlAbs + (productUrlAbs.includes('?') ? '&' : '?') + 'view=specs' + cacheBuster;

    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Failed to load specs view');
      
      const text = await res.text();
      
      // Создаем временный элемент, чтобы вытащить только нужный нам div
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const specsRoot = doc.querySelector('[data-ps-specs-root]');
      
      return specsRoot ? specsRoot.innerHTML : text;
    } catch (e) {
      console.error('[ps-shell-picker] Error loading specs:', e);
      return '';
    }
  }

  async function loadProductSpecsHtml(productUrlRaw, extraParams = {}) {
    const productUrlAbs = toAbsUrl(productUrlRaw);
    if (!productUrlAbs) return '';

    const cacheKey = `${productUrlAbs}|${JSON.stringify(extraParams || {})}`;
    if (productSpecsCache.has(cacheKey)) return productSpecsCache.get(cacheKey) || '';

    const html = await loadSpecsViaSectionId(productUrlAbs);
    productSpecsCache.set(cacheKey, html || '');
    return html || '';
  }

  // =========================================================
  // 6) Selection modal
  // =========================================================
  function createPsModalShell({ type, title = '', bodyHtml = '', footerHtml = '' } = {}) {
    const root = document.createElement('div');
    root.className = `ps-modal ps-modal--${type || 'generic'}`;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.hidden = true;

    root.innerHTML = `
      <div class="ps-modal__overlay" data-ps-modal-close></div>
      <div class="ps-modal__dialog" role="document">
        <div class="ps-modal__head">
          <h3 class="ps-modal__title"></h3>
          <button type="button" class="ps-modal__close" data-ps-modal-close aria-label="Close">
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div class="ps-modal__body"></div>
        <div class="ps-modal__footer"></div>
      </div>
    `;

    const titleEl = root.querySelector('.ps-modal__title');
    const bodyEl = root.querySelector('.ps-modal__body');
    const footerEl = root.querySelector('.ps-modal__footer');

    titleEl.textContent = title || '';
    bodyEl.innerHTML = bodyHtml || '';
    footerEl.innerHTML = footerHtml || '';

    return { root, titleEl, bodyEl, footerEl };
  }

  function ensureSelectionModal(pickerEl) {
    let modal = pickerEl.querySelector('[data-ps-selection-modal]');
    if (modal) return modal;

    const { root, titleEl } = createPsModalShell({
      type: 'selection',
      title: 'Your selection',
      bodyHtml: `<div class="ps-selection-list" data-ps-selection-list></div>`,
      footerHtml: `
        <div class="ps-selection-modal__footer">
          <button type="button" class="ps-btn ps-btn--secondary" data-ps-selection-clear>Clear all</button>
          <div class="ps-selection-modal__footer-right">
            <button type="button" class="ps-btn ps-btn--primary" data-ps-selection-quote>Get a Quote</button>
          </div>
        </div>
      `
    });

    root.classList.add('ps-selection-modal');
    root.setAttribute('data-ps-selection-modal', '');
    root.hidden = true;

    titleEl.classList.add('ps-selection-modal__title');
    titleEl.insertAdjacentHTML(
      'afterend',
      `<p class="ps-selection-modal__sub" data-ps-selection-sub>0 shells selected</p>`
    );

    const overlay = root.querySelector('.ps-modal__overlay');
    const dialog = root.querySelector('.ps-modal__dialog');
    const head = root.querySelector('.ps-modal__head');
    const closeBtn = root.querySelector('.ps-modal__close');

    if (overlay) overlay.classList.add('ps-selection-modal__overlay');
    if (dialog) dialog.classList.add('ps-selection-modal__dialog');
    if (head) head.classList.add('ps-selection-modal__head');
    if (closeBtn) closeBtn.classList.add('ps-selection-modal__close');

    const productModal = pickerEl.querySelector('.ps-product-modal');
    if (productModal) pickerEl.insertBefore(root, productModal);
    else pickerEl.appendChild(root);

    return root;
  }

  function openSelectionModal(pickerEl) {
    const modal = ensureSelectionModal(pickerEl);
    openPsModal(modal);
    requestAnimationFrame(remeasureAllBars);
  }

  function closeSelectionModal(pickerEl) {
    const modal = pickerEl.querySelector('[data-ps-selection-modal]');
    if (!modal) return;
    closePsModal(modal);
    requestAnimationFrame(remeasureAllBars);
  }

  function renderSelectionModal(pickerEl, storageKey) {
    const modal = ensureSelectionModal(pickerEl);
    const items = readSelected(storageKey);

    const sub = modal.querySelector('[data-ps-selection-sub]');
    const list = modal.querySelector('[data-ps-selection-list]');

    if (sub) sub.textContent = `${items.length} shell${items.length === 1 ? '' : 's'} selected`;
    if (!list) return;

    if (items.length === 0) {
      list.innerHTML = `<div style="opacity:.8; padding: 8px 0;">No shells selected yet.</div>`;
      return;
    }

    list.innerHTML = items.map(x => {
      const id = String(x.id || '');
      const title = String(x.title || '').trim();
      const card = pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(id)}"]`);
      const thumb = (card?.getAttribute('data-thumb') || '').trim();
      const sizeLine = (card?.getAttribute('data-size-line') || '').trim();
      const label = `${title}${sizeLine ? ` ${sizeLine}` : ''}`.trim();

      return `
        <div class="ps-selection-row" data-ps-selection-row data-product-id="${escapeAttr(id)}">
          <div class="ps-selection-row__left">
            ${thumb ? `<img class="ps-selection-row__thumb" src="${escapeAttr(thumb)}" alt="" loading="lazy" width="44" height="44">` : ``}
            <button type="button" class="ps-selection-row__title" data-ps-selection-open data-product-id="${escapeAttr(id)}">
              ${escapeHtml(label)}
            </button>
          </div>
          <div class="ps-selection-row__actions">
            <button type="button" class="ps-btn ps-btn--secondary" data-ps-selection-remove data-product-id="${escapeAttr(id)}">Remove</button>
          </div>
        </div>
      `;
    }).join('');
  }

  // =========================================================
  // 6.5) Quote modal + suburb autocomplete
  // =========================================================
  const DEFAULT_POSTCODES_URL = '/assets/au-postcodes.json';
  let postcodesPromise = null;

  function getPostcodesUrl(pickerEl) {
    return pickerEl.getAttribute('data-postcodes-url') || DEFAULT_POSTCODES_URL;
  }

  function loadAuPostcodes(pickerEl) {
    if (postcodesPromise) return postcodesPromise;
    const url = getPostcodesUrl(pickerEl);

    postcodesPromise = fetch(url, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`Failed to load ${url}`))))
      .then(data => {
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.rows)) return data.rows;
        if (data && typeof data === 'object') {
          const out = [];
          for (const [postcode, arr] of Object.entries(data)) {
            if (!Array.isArray(arr)) continue;
            for (const it of arr) {
              const suburb = String(it?.suburb ?? it?.Suburb ?? it?.locality ?? '').trim();
              const state = String(it?.state ?? it?.State ?? '').trim();
              if (!postcode || !suburb) continue;
              out.push({ postcode: String(postcode).trim(), suburb, state });
            }
          }
          return out;
        }
        return [];
      })
      .catch(() => []);

    return postcodesPromise;
  }

  function getQuoteModal(pickerEl) {
    return pickerEl.querySelector('[data-ps-quote-modal]');
  }

  function openQuoteModal(pickerEl) {
    const modal = getQuoteModal(pickerEl);
    if (!modal) return;
    openPsModal(modal);
    requestAnimationFrame(remeasureAllBars);
  }

  function closeQuoteModal(pickerEl) {
    const modal = getQuoteModal(pickerEl);
    if (!modal) return;
    closePsModal(modal);
    requestAnimationFrame(remeasureAllBars);
  }

  function renderQuoteSummary(pickerEl, items) {
    const modal = getQuoteModal(pickerEl);
    if (!modal) return;

    const box = modal.querySelector('[data-ps-quote-summary]');
    if (!box) return;

    if (!items || !items.length) {
      box.innerHTML = '';
      box.hidden = true;
      return;
    }

    const list = items.map(x => {
      const id = String(x.id || '');
      const card = pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(id)}"]`);
      const thumb = (card?.getAttribute('data-thumb') || '').trim();
      const sizeLine = (card?.getAttribute('data-size-line') || '').trim();
      const title = String(x.title || '').trim();
      const label = `${title}${sizeLine ? ` ${sizeLine}` : ''}`.trim();

      return `
        <li class="ps-quote-summary__item">
          ${thumb ? `<img class="ps-quote-summary__thumb" src="${escapeAttr(thumb)}" alt="" loading="lazy" width="44" height="44">` : ``}
          <div class="ps-quote-summary__text">${escapeHtml(label)}</div>
        </li>
      `;
    }).join('');

    box.hidden = false;
    box.innerHTML = `
      <div class="ps-quote-summary__title">Selected shells</div>
      <ul class="ps-quote-summary__list">${list}</ul>
    `;
  }

  function setQuoteStatus(pickerEl, status, message) {
    const modal = getQuoteModal(pickerEl);
    if (!modal) return;

    const box = modal.querySelector('[data-ps-quote-status]');
    if (!box) return;

    box.hidden = !status;
    box.textContent = message || '';
    box.setAttribute('data-status', status || '');
  }

  function klaviyoTrackQuote({ email, phone, name, suburb, message, shells }) {
    try {
      const q = window._learnq;
      if (!Array.isArray(q) && !q?.push) return;

      if (email) {
        q.push(['identify', {
          $email: email,
          $phone_number: phone || undefined,
          $first_name: name || undefined
        }]);
      }

      q.push(['track', 'Pool Shell Quote Requested', {
        shells: shells || [],
        suburb: suburb || '',
        message: message || '',
        source: 'ps-shell-picker'
      }]);
    } catch {}
  }

  function clearAllAndReset(pickerEl, storageKey) {
    const empty = clearSelected(storageKey);
    refreshCards(pickerEl, empty);
    syncHiddenInputs(pickerEl, empty);
    updateQuoteBar(pickerEl, empty);
    renderSelectionModal(pickerEl, storageKey);
    renderQuoteSummary(pickerEl, empty);
    applyAllFilters(pickerEl, storageKey);
  }

  // suburb dropdown
  function bindSuburbAutocomplete(pickerEl, modal) {
    const suburbInput = modal.querySelector('[data-ps-suburb-input]');
    const suburbMenu  = modal.querySelector('[data-ps-suburb-menu]');
    if (!suburbInput || !suburbMenu) return;

    let lastQ = '';
    let acTimer = null;
    let activeIndex = -1;

    function closeMenu() {
      suburbMenu.hidden = true;
      suburbMenu.innerHTML = '';
      suburbMenu.setAttribute('aria-hidden', 'true');
      activeIndex = -1;
    }

    function openMenu() {
      suburbMenu.hidden = false;
      suburbMenu.setAttribute('aria-hidden', 'false');
    }

    function setActive(items) {
      items.forEach((el, i) => el.classList.toggle('is-active', i === activeIndex));
      if (activeIndex >= 0 && items[activeIndex]) items[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    function renderMenu(options) {
      if (!options.length) { closeMenu(); return; }
      suburbMenu.innerHTML = options.map((x, i) => {
        const label = `${x.postcode} - ${x.suburb}${x.state ? ` (${x.state})` : ''}`;
        return `<button type="button" class="ps-ac__item" data-i="${i}">${escapeHtml(label)}</button>`;
      }).join('');
      openMenu();
    }

    async function updateMenu(q) {
      const query = String(q || '').trim();
      const ql = query.toLowerCase();
      if (ql === lastQ) return;
      lastQ = ql;

      if (!ql) { closeMenu(); return; }

      const rows = await loadAuPostcodes(pickerEl);
      const isDigits = /^[0-9]/.test(ql);
      if (!isDigits && ql.length < 2) { closeMenu(); return; }

      const out = [];
      const seen = new Set();

      for (const r of rows) {
        const postcode = String(r.postcode ?? r.Postcode ?? r.post_code ?? '').trim();
        const suburb = String(r.suburb ?? r.Suburb ?? r.locality ?? '').trim();
        const state = String(r.state ?? r.State ?? '').trim();
        if (!postcode || !suburb) continue;

        const key = `${postcode}|${suburb}`.toLowerCase();
        if (seen.has(key)) continue;

        const match = isDigits
          ? postcode.toLowerCase().startsWith(ql)
          : (suburb.toLowerCase().startsWith(ql) || suburb.toLowerCase().includes(ql));

        if (!match) continue;

        seen.add(key);
        out.push({ postcode, suburb, state });
        if (out.length >= 40) break;
      }

      activeIndex = -1;
      renderMenu(out);
    }

    suburbInput.addEventListener('input', () => {
      clearTimeout(acTimer);
      acTimer = setTimeout(() => updateMenu(suburbInput.value), 80);
    });

    suburbInput.addEventListener('focus', () => {
      loadAuPostcodes(pickerEl);
      updateMenu(suburbInput.value);
    });

    suburbInput.addEventListener('blur', () => setTimeout(closeMenu, 140));
    suburbMenu.addEventListener('mousedown', (e) => e.preventDefault());

    suburbMenu.addEventListener('click', (e) => {
      const btn = e.target.closest('.ps-ac__item');
      if (!btn) return;
      suburbInput.value = btn.textContent.trim();
      closeMenu();
      suburbInput.focus();
    });

    suburbInput.addEventListener('keydown', (e) => {
      if (suburbMenu.hidden) return;

      const items = [...suburbMenu.querySelectorAll('.ps-ac__item')];
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(items.length - 1, activeIndex + 1);
        setActive(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(0, activeIndex - 1);
        setActive(items);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && items[activeIndex]) {
          e.preventDefault();
          suburbInput.value = items[activeIndex].textContent.trim();
          closeMenu();
        }
      } else if (e.key === 'Escape') {
        closeMenu();
      }
    });
  }

  function bindQuoteForm(pickerEl, storageKey, scrollOffset) {
    const modal = getQuoteModal(pickerEl);
    if (!modal) return;

    const form = modal.querySelector('form');
    if (!form) return;

    bindSuburbAutocomplete(pickerEl, modal);

    if (form.__psBound) return;
    form.__psBound = true;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation(); // чтобы другие submit-хендлеры (если есть) не вмешивались

      if (form.__psSending) return;
      form.__psSending = true;

      const items = readSelected(storageKey);
      if (!items.length) {
        setQuoteStatus(pickerEl, 'error', 'Please select at least one shell first.');
        form.__psSending = false; // ✅ FIX: не залипаем
        return;
      }

      syncHiddenInputs(pickerEl, items);
      setQuoteStatus(pickerEl, 'sending', 'Sending…');

      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      try {
        const fd = new FormData(form);

        // Берём shellsText из hidden input (который syncHiddenInputs заполняет)
        const shellsEl = form.querySelector('[data-ps-selected-items]');
        let shellsText = shellsEl ? String(shellsEl.value || '').trim() : '';

        // fallback: если hidden input пустой — соберём из items
        if (!shellsText) {
          shellsText = items.map(x => {
            const id = String(x.id || '');
            const card = pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(id)}"]`);
            const title = String(x.title || '').trim();
            const sizeLine = (card?.getAttribute('data-size-line') || '').trim();
            return `${title}${sizeLine ? ` ${sizeLine}` : ''}`.trim();
          }).filter(Boolean).join('\n');
        }

        const payload = {
          name: String(fd.get('contact[name]') || '').trim(),
          email: String(fd.get('contact[email]') || '').trim(),
          phone: String(fd.get('contact[phone]') || '').trim(),
          suburb: String(fd.get('contact[suburb]') || '').trim(),
          message: String(fd.get('contact[body]') || '').trim(),
          shellsText
        };

        const res = await fetch("https://api.pooltools.au/send", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        console.log('[ps-shell-picker] send status:', res.status, 'body:', text);

        if (!res.ok) throw new Error(text || `Request failed (${res.status})`);

        // Worker теперь отвечает JSON; попробуем вытащить reqId/customerSent
        let parsed = null;
        try { parsed = JSON.parse(text); } catch {}

        const reqId = parsed?.reqId ? String(parsed.reqId) : '';
        const customerSent = parsed?.customerSent;

        // сообщение пользователю
        if (reqId) {
          if (customerSent === false) {
            setQuoteStatus(pickerEl, 'success', `Sent! Ref: ${reqId}. (Auto-reply may be delayed)`);
          } else {
            setQuoteStatus(pickerEl, 'success', `Thanks! Your request has been sent. Ref: ${reqId}`);
          }
        } else {
          setQuoteStatus(pickerEl, 'success', 'Thanks! Your request has been sent.');
        }

        // Klaviyo tracking
        const email = String(fd.get('contact[email]') || '');
        const phone = String(fd.get('contact[phone]') || '');
        const name = String(fd.get('contact[name]') || '');
        const suburb = String(fd.get('contact[suburb]') || '');
        const message = String(fd.get('contact[body]') || '');

        const shells = items.map(x => {
          const id = String(x.id || '');
          const card = pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(id)}"]`);
          return { title: String(x.title || ''), size: (card?.getAttribute('data-size-line') || '').trim() };
        });

        klaviyoTrackQuote({ email, phone, name, suburb, message, shells });

        setTimeout(() => {
          try { form.reset(); } catch {}
          clearAllAndReset(pickerEl, storageKey);
          closeQuoteModal(pickerEl);

          const y = pickerEl.getBoundingClientRect().top + window.pageYOffset - (scrollOffset || 0);
          window.scrollTo({ top: y, behavior: 'smooth' });

          setQuoteStatus(pickerEl, '', '');
        }, 900);
      } catch (err) {
        console.error('[ps-shell-picker] send failed:', err);
        setQuoteStatus(pickerEl, 'error', `Sorry — something went wrong. ${String(err?.message || '')}`.trim());
      } finally {
        if (submitBtn) submitBtn.disabled = false;
        form.__psSending = false;
      }
    });
  }


  // =========================================================
  // 8) Init per picker
  // =========================================================
  function initPicker(pickerEl) {
    const storageKey = pickerEl.getAttribute('data-storage-key') || DEFAULT_STORAGE_KEY;
    const scrollOffset = Number(pickerEl.getAttribute('data-scroll-offset') || 0);

    // Инициализация фильтров и состояния
    initTabs(pickerEl, storageKey);
    bindSpaFilter(pickerEl, storageKey);

    const items = readSelected(storageKey);
    refreshCards(pickerEl, items);
    syncHiddenInputs(pickerEl, items);
    updateQuoteBar(pickerEl, items);
    bindQuoteForm(pickerEl, storageKey, scrollOffset);

    // Единый слушатель кликов для всего компонента
    pickerEl.addEventListener('click', async (e) => {
      
      // 1. Клик по кнопкам закрытия модалок
      const closeHit = e.target.closest('[data-ps-modal-close]');
      if (closeHit) {
        const modalEl = closeHit.closest('.ps-modal');
        if (modalEl) {
          closePsModal(modalEl);
          return;
        }
      }

      // 2. Клик по кнопке "Add To Quote" (в карточке или в модалке)
      const toggleBtn = e.target.closest('[data-ps-toggle]') || e.target.closest('[data-ps-modal-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation(); // Важно: не даем клику "уйти" в карточку и открыть модалку

        const isModalBtn = toggleBtn.hasAttribute('data-ps-modal-toggle');
        const card = isModalBtn 
          ? pickerEl.querySelector(`.ps-shell-picker__item[data-product-id="${cssEscape(toggleBtn.getAttribute('data-product-id'))}"]`)
          : toggleBtn.closest('.ps-shell-picker__item');

        if (!card) return;
        const item = {
          id: card.getAttribute('data-product-id'),
          title: card.getAttribute('data-title'),
          url: card.getAttribute('data-url'),
          sku: card.getAttribute('data-sku')
        };

        const newItems = toggleSelection(storageKey, item);
        const selectedNow = newItems.some(x => String(x.id) === String(item.id));

        // Обновляем текст кнопки (если в модалке)
        if (isModalBtn) {
          toggleBtn.textContent = selectedNow ? 'Added' : 'Add to quote';
          toggleBtn.setAttribute('aria-pressed', selectedNow ? 'true' : 'false');
        }

        // Синхронизируем карточку, бар и инпуты
        if (card) updateCardUI(card, selectedNow);
        syncHiddenInputs(pickerEl, newItems);
        updateQuoteBar(pickerEl, newItems);
        applyAllFilters(pickerEl, storageKey);
        return;
      }

      // 3. Клик по всей карточке (открывает модалку)
      const card = e.target.closest('.ps-shell-picker__item');
      if (card) {
        // Если кликнули не по кнопке выбора и не по ссылке внутри
        const isAction = e.target.closest('.ps-shell-picker__actions');
        if (!isAction) {
          closeSelectionModal(pickerEl);
          fillProductModalFromCard(pickerEl, storageKey, card);
          openProductModal(pickerEl);
          return;
        }
      }

      // 4. Кнопка "View full specs" внутри модалки
      const more = e.target.closest('[data-ps-modal-more]');
      if (more) {
        const els = getModalEls(pickerEl);
        if (!els || !els.modal || !els.details) return;

        if (!els.details.hidden) {
          els.details.hidden = true;
          els.details.innerHTML = '';
          more.textContent = 'View full specs →';
          more.setAttribute('aria-expanded', 'false');
          return;
        }

        if (more.dataset.loading === '1') return;
        more.dataset.loading = '1';

        const productUrl = card?.getAttribute('data-url') || els.modal.querySelector('[data-ps-modal-toggle]')?.getAttribute('data-url');
        more.disabled = true;
        more.textContent = 'Loading…';

        try {
          const html = await loadProductSpecsHtml(productUrl);
          els.details.innerHTML = html || `<div style="opacity:.75;padding-top:10px;">No additional info.</div>`;
          els.details.hidden = false;
          more.textContent = 'Less specs ←';
          more.setAttribute('aria-expanded', 'true');
        } catch {
          els.details.innerHTML = `<div style="opacity:.75;padding-top:10px;">Error loading data.</div>`;
          els.details.hidden = false;
        } finally {
          delete more.dataset.loading;
          more.disabled = false;
        }
        return;
      }

      // 5. Остальные системные клики (Бар цитаты, Окно выбора и т.д.)
      const viewSel = e.target.closest('[data-ps-view-selection]');
      if (viewSel) {
        closeProductModal(pickerEl);
        renderSelectionModal(pickerEl, storageKey);
        openSelectionModal(pickerEl);
        return;
      }

      const selOpen = e.target.closest('[data-ps-selection-open]');
      if (selOpen) {
        closeSelectionModal(pickerEl);
        openProductModalById(pickerEl, storageKey, selOpen.getAttribute('data-product-id'));
        return;
      }

      const selRemove = e.target.closest('[data-ps-selection-remove]');
      if (selRemove) {
        const newItems = removeSelectedById(storageKey, selRemove.getAttribute('data-product-id'));
        refreshCards(pickerEl, newItems);
        syncHiddenInputs(pickerEl, newItems);
        updateQuoteBar(pickerEl, newItems);
        renderSelectionModal(pickerEl, storageKey);
        applyAllFilters(pickerEl, storageKey);
        return;
      }

      const selClear = e.target.closest('[data-ps-selection-clear]');
      if (selClear) {
        const newItems = clearSelected(storageKey);
        refreshCards(pickerEl, newItems);
        syncHiddenInputs(pickerEl, newItems);
        updateQuoteBar(pickerEl, newItems);
        renderSelectionModal(pickerEl, storageKey);
        applyAllFilters(pickerEl, storageKey);
        return;
      }

      const selQuote = e.target.closest('[data-ps-selection-quote]');
      if (selQuote) {
        closeProductModal(pickerEl);
        closeSelectionModal(pickerEl);
        renderQuoteSummary(pickerEl, readSelected(storageKey));
        openQuoteModal(pickerEl);
        return;
      }
    });
  }

  // =========================================================
  // Boot
  // =========================================================
  function boot() {
    document.querySelectorAll('ps-shell-picker').forEach(initPicker);
    remeasureAllBars();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // =========================================================
  // Utils
  // =========================================================
  function decodeHtmlEntities(str) {
    const el = document.createElement('textarea');
    el.innerHTML = String(str || '');
    return el.value;
  }

  function escapeAttr(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;')
      .replaceAll('`', '&#096;');
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;')
      .replaceAll('`', '&#096;');
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  }
})();
